import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useRouter } from 'next/router'

import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { LanguageContext } from '@lib/language-context'
import { getShopifyCartIdStorageKey } from '@lib/local-storage'
import { getLinkPageUrl } from '@lib/routes'
import { type LocalizedShop, ShopContext } from '@lib/shop-context'
import { emptyCart, getOrCreateCart } from './helpers'
import { useUpdateCartShippingSettings } from './hooks'
import { type Cart } from './types'

interface CartContextProps {
  cart: Cart
  isCartOpen: boolean
  isCartUpdating: boolean
  isCreatingProduct: boolean
  cartAddingProductIds: number[]
  isCartSubmitting: boolean
  openCartInModal?: boolean
  toggleCart: (newState: boolean) => void
  saveCart: (cart?: Cart) => void
  setIsCartUpdating: (isCartUpdating: boolean) => void
  setIsCreatingProduct: (isCreatingProduct: boolean) => void
  setCartAddingProductIds: (cartAddingProductIds: number[]) => void
  setIsCartSubmitting: (isCartSubmitting: boolean) => void
}

interface CartContextProviderProps {
  site: SanitySiteFragment
  localizedShop: LocalizedShop
  children: ReactNode
}

export const CartContext = createContext<CartContextProps>({
  cart: emptyCart,
  isCartOpen: false,
  isCartUpdating: false,
  isCreatingProduct: false,
  cartAddingProductIds: [],
  isCartSubmitting: false,
  toggleCart: () => null,
  saveCart: () => null,
  setIsCartUpdating: () => null,
  setIsCreatingProduct: () => null,
  setCartAddingProductIds: () => null,
  setIsCartSubmitting: async () => null,
})

/**
 * The cart context provider.
 */
export const CartContextProvider = ({
  site,
  localizedShop,
  children,
}: CartContextProviderProps) => {
  const { locale } = useContext(LanguageContext)
  const { shopifyStorefrontGraphQlClient } = useContext(ShopContext)

  const [cart, setCart] = useState<Cart>(emptyCart)
  const [openCartInModal, setOpenCartInModal] = useState(
    !site.cartSettings?.openInSeparatePage,
  )
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [isCartUpdating, setIsCartUpdating] = useState(false)
  const [isCreatingProduct, setIsCreatingProduct] = useState(false)
  const [cartAddingProductIds, setCartAddingProductIds] = useState<number[]>([])
  const [isCartSubmitting, setIsCartSubmitting] = useState(false)

  const router = useRouter()

  const saveCart = useCallback(
    (cart?: Cart) => {
      if (!cart) {
        return
      }

      setCart(cart)

      const shopifyCartIdStorageKey = getShopifyCartIdStorageKey(locale)
      localStorage.setItem(shopifyCartIdStorageKey, cart.id)
    },
    [locale],
  )

  const toggleCart = useCallback(
    (newState: boolean) => {
      if (!openCartInModal) {
        if (newState) {
          const url = getLinkPageUrl({
            pageType: 'cartPage',
          })
          router.push(url, url, {
            locale,
          })
        }

        return
      }

      if (isCartOpen !== newState) {
        setIsCartOpen(newState)
      }
    },
    [isCartOpen, openCartInModal, locale, router],
  )

  // Load cart settings (update when switching language)
  useEffect(() => {
    setOpenCartInModal(!site.cartSettings?.openInSeparatePage)
  }, [site.cartSettings])

  // Load cart from Shopify
  useEffect(() => {
    if (
      !shopifyStorefrontGraphQlClient ||
      !localizedShop.shop ||
      localizedShop.locale !== locale
    ) {
      return
    }

    const loadCart = async () => {
      const newCart = await getOrCreateCart(
        locale,
        shopifyStorefrontGraphQlClient,
        localizedShop.cart,
      )
      saveCart(newCart)
    }
    loadCart()
  }, [locale, saveCart, localizedShop, shopifyStorefrontGraphQlClient])

  useUpdateCartShippingSettings(cart, saveCart)

  return (
    <CartContext.Provider
      value={{
        cart,
        isCartOpen,
        isCartUpdating,
        isCreatingProduct,
        cartAddingProductIds,
        isCartSubmitting,
        openCartInModal,
        toggleCart,
        saveCart,
        setIsCartUpdating,
        setIsCreatingProduct,
        setCartAddingProductIds,
        setIsCartSubmitting,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}
